import { useState } from "react"
import { createPortal } from "react-dom"

import SuccessModal from "app/components/billing/success_modal"
import CreditsPlanChangeModal from "app/components/credits_upgrade/plan_change_modal"

import { lockupLogoSvg } from "shared/utils/ifttt_logo"
import { planNameBadge } from "shared/utils/tiers"

interface Props {
  productId: string
  planName: string
  planTier: string
  cta: string
  priceLine: string
  subscriptionPaymentType: string
  buttonClass?: string
  // Set only for a downgrade, which IFE schedules for this date instead of applying it now.
  effectiveDate?: string | null
  currentPlanName?: string
}

// Plan-change button for users on the credits billing system: switches an
// existing Stripe subscription to another credits plan in place, mirroring
// PlanDowngradeButton (credits plans have no feature gates to enumerate, so
// the confirmation copy is just the target plan and its price).
const CreditsPlanChangeButton = ({
  productId,
  planName,
  planTier,
  cta,
  priceLine,
  subscriptionPaymentType,
  buttonClass = "button-secondary",
  effectiveDate,
  currentPlanName,
}: Props) => {
  const [showModal, setShowModal] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  if (subscriptionPaymentType !== "stripe") return null

  const handleClick = (evt: MouseEvent) => {
    evt.preventDefault()
    setShowModal(true)
  }

  if (showSuccess) {
    return createPortal(
      <SuccessModal
        title={effectiveDate ? "You're moving to" : "You've switched to"}
        badge={lockupLogoSvg() + planNameBadge(planName)}
        successContent={
          effectiveDate && (
            <p>
              You will be moved to {planName} on {effectiveDate}.
            </p>
          )
        }
        visible={true}
        onClose={() => {
          window.location.href = "/billing"
        }}
        buttons={[
          {
            label: "Back to Billing",
            onClick: () => {
              window.location.href = "/billing"
            },
            variant: "secondary",
          },
          {
            label: "Explore Applets",
            onClick: () => {
              window.location.href = "/explore"
            },
            variant: "primary",
          },
        ]}
      />,
      document.body,
    )
  }

  return (
    <>
      <button className={buttonClass} data-plan-tier={planTier} onClick={handleClick}>
        {cta}
      </button>
      {showModal && (
        <CreditsPlanChangeModal
          productId={productId}
          planName={planName}
          cta={cta}
          priceLine={priceLine}
          effectiveDate={effectiveDate}
          currentPlanName={currentPlanName}
          onClose={() => setShowModal(false)}
          onSwitched={() => {
            setShowModal(false)
            setShowSuccess(true)
          }}
        />
      )}
    </>
  )
}

export default CreditsPlanChangeButton
